import React from 'react';
import { MoreVert, PersonOutline, Payment, Phone } from '@material-ui/icons';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { Modal, Button, Typography, Box } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as apiService from './apiService';

const ITEM_HEIGHT = 48;

function RemovePhoneModal({ params, openModal, setOpenModal }) {
  const [loading, setLoading] = React.useState(false);
  const currentPhone = params.data?.phone;

  const handleRemove = async () => {
    setLoading(true);
    try {
      await apiService.removeStudentPhone({ userId: params.data.id });
      toast.success('Phone number removed successfully');
      setOpenModal(false);
      if (params.context?.refreshGrid) params.context.refreshGrid();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to remove phone number';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={openModal} onClose={() => setOpenModal(false)}>
      <div className='relative overflow-y-auto max-h-96 top-2 md:top-10 m-auto bg-white rounded-lg md:px-8 px-3 py-4 z-50 lg:w-1/3 w-11/12 border-solid border border-gray-300'>
        <div className='absolute right-4 top-4 cursor-pointer text-gray-600 hover:text-gray-900' onClick={() => setOpenModal(false)}>✕</div>
        <Typography variant='h6' component='h2' className='mb-4 pr-8'>Remove Phone Number</Typography>
        <Typography variant='body1' className='mb-4'>
          Are you sure you want to remove the phone number <strong>{currentPhone}</strong> from this user&apos;s profile?
        </Typography>
        <Typography variant='caption' className='text-gray-600 mb-4 block'>
          This action cannot be undone. The phone number will be permanently deleted.
        </Typography>
        <Box className='flex justify-end gap-2 mt-6'>
          <Button variant='outlined' onClick={() => setOpenModal(false)} disabled={loading} className='text-gray-600 border-gray-300'>Cancel</Button>
          <Button variant='contained' color='secondary' onClick={handleRemove} disabled={loading}>
            {loading ? 'Removing...' : 'Remove Phone'}
          </Button>
        </Box>
      </div>
    </Modal>
  );
}

export function FreeUserActionCellRenderer(params) {
  const history = useHistory();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [isInvited, setIsInvited] = React.useState(false);
  const [removePhoneOpen, setRemovePhoneOpen] = React.useState(false);
  const open = Boolean(anchorEl);

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleInvite = async () => {
    handleClose();
    setIsInvited(true);
    try {
      await apiService.inviteForDemoClass(params.data?.id);
      toast.success('Invited for demo class');
    } catch {
      toast.error('Failed to invite for demo class');
      setIsInvited(false);
    }
  };

  const handleViewProfile = () => {
    handleClose();
    history.push({ pathname: '/all-users/user-details', state: { userParams: params.data } });
  };

  const handleEnrollPayment = () => {
    handleClose();
    history.push({ pathname: '/all-users/enroll-payment', state: { userParams: params.data } });
  };

  const handleRemovePhone = () => {
    handleClose();
    setRemovePhoneOpen(true);
  };

  return (
    <div>
      <IconButton
        aria-label="more"
        aria-controls={open ? 'free-user-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleOpen}
      >
        <MoreVert />
      </IconButton>

      <Menu
        id="free-user-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{ style: { maxHeight: ITEM_HEIGHT * 6, width: '20ch' } }}
      >
        <MenuItem onClick={handleViewProfile}>
          <div className="flex gap-1 items-center font-normal">
            <PersonOutline fontSize="small" />
            View Profile
          </div>
        </MenuItem>
        <MenuItem onClick={handleInvite} disabled={isInvited}>
          <div className="flex gap-1 items-center font-normal">
            {isInvited ? 'Invited ✓' : 'Invite For Demo Class'}
          </div>
        </MenuItem>
        <MenuItem onClick={handleEnrollPayment}>
          <div className="flex gap-1 items-center font-normal">
            <Payment fontSize="small" />
            Enroll Payment
          </div>
        </MenuItem>
        {params.data?.phone && (
          <MenuItem onClick={handleRemovePhone}>
            <div className="flex gap-1 items-center font-normal">
              <Phone fontSize="small" />
              Remove Phone
            </div>
          </MenuItem>
        )}
      </Menu>

      <RemovePhoneModal
        params={params}
        openModal={removePhoneOpen}
        setOpenModal={setRemovePhoneOpen}
      />
    </div>
  );
}
